import { appInsightService } from "./AppInsightService";
import { SeverityLevel } from "@microsoft/applicationinsights-common";
import { toastService } from "./ToastService";

export interface LogFn {
    (message?: any, ...optionalParams: any[]): void;
}

/** logger interface */
export interface LogActions {
    info: LogFn;
    log: LogFn;
    error: LogFn;
    exception: LogFn;
    event: LogFn;
    warning: LogFn;
};

let loggerPrefix = "RPG UI - ";
if ((process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "development") || (process.env.NODE_ENV?.toLowerCase() === "development")) {
    loggerPrefix += "DEV - ";
}

const setParams = (params: any) => {
    if (!params || params.length === 0 || params[0].length === 0 || params[0] === undefined || params[0] === null || JSON.stringify(params) === "[{}]") {
        return undefined;
    }

    let retDict = {};

    const retArr: string[] = [];
    params.forEach((paramArray: any) => {
        if (paramArray.length > 0) {
            paramArray.forEach((entry: any) => {
                Object.entries(entry).forEach(([key, value]) => {
                    //console.log(`Key: ${key}, Value: ${JSON.stringify(value)}`);
                    //console.log(value);
                    retArr.push(`{ ${key}: ${JSON.stringify(value)} }`);
                    retDict = { ...retDict, [key]: value };
                });
            });
        }
    });

    return retDict;
    //return retArr;
}

const trackLogOrInfo = (message: string, ...optionalParams: any[]) => {
    let obj = {
        message: loggerPrefix + message,
        properties: setParams(optionalParams),
        severityLevel: SeverityLevel.Information
    };

    if ((process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "development") || (process.env.NODE_ENV?.toLowerCase() === "development")) {
        if (obj.properties && obj.properties !== "{}") {
            console.info(obj.message + ": " + JSON.stringify(obj.properties));
        } else {
            console.info(obj.message);
        }
        return;
    }

    let appInsights = appInsightService;
    if (appInsights) {
        appInsights.trackTrace(obj);
    } else {
        console.error("AppInsights is not available...");
    }
};

export class AppInsightLogger implements LogActions {
    info = (message: any, ...optionalParams: any[]) => trackLogOrInfo(message, optionalParams);

    log = (message: any, ...optionalParams: any[]) => trackLogOrInfo(message, optionalParams);

    error = (error: any, ...optionalParams: any[]) => {
        let obj = {
            message: loggerPrefix + error,
            properties: setParams(optionalParams),
            severityLevel: SeverityLevel.Error
        };
        
        let toastText = error;
        if (obj.properties && obj.properties !== "{}") {
            toastText += " " + obj.properties;
        }
        toastService.error(toastText);

        if ((process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "development") || (process.env.NODE_ENV?.toLowerCase() === "development")) {
            console.error(obj.message + ": " + JSON.stringify(obj.properties));
            return;
        }
    
        let appInsights = appInsightService;
        if (appInsights) {
            appInsights.trackTrace(obj);
        } else {
            console.info("AppInsights is not available...");
            console.error(obj.message);
        }
    };

    exception = (error: any, ...optionalParams: any[]) => {
        let objError = {
            message: loggerPrefix + "Exception - " + error,
            properties: setParams(optionalParams),
            severityLevel: SeverityLevel.Error
        };

        let objException = {
            error: new Error(loggerPrefix + error),
            properties: setParams(optionalParams),
            severityLevel: SeverityLevel.Error
        };
    
        if ((process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "development") || (process.env.NODE_ENV?.toLowerCase() === "development")) {
            console.error(objException.error + ": " + JSON.stringify(objException.properties));
            return;
        }
    
        let appInsights = appInsightService;
        if (appInsights) {
            appInsights.trackException(objException);
            appInsights.trackTrace(objError);
        } else {
            console.info("AppInsights is not available...");
            console.error(objException.error);
        }
    };

    event = (event: any, ...optionalParams: any[]) => {
        let objEvent = {
            name: event,
            properties: setParams(optionalParams),
        };

        let objInfo = {
            message: loggerPrefix + "Event - " + event,
            properties: setParams(optionalParams),
            severityLevel: SeverityLevel.Information
        };

        if ((process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "development") || (process.env.NODE_ENV?.toLowerCase() === "development")) {
            console.info("Event: " + objEvent.name);
            return;
        }

        let appInsights = appInsightService;
        if (appInsights) {
            appInsights.trackEvent(objEvent);
            // // Example of tracking an event
            // appInsights.trackEvent({ name: 'ButtonClick', properties: { buttonId: 'submit' } });
            appInsights.trackTrace(objInfo);
        } else {
            console.error("AppInsights is not available...");
        }
    };

    warning = (message: any, ...optionalParams: any[]) => {
        let obj = {
            message: loggerPrefix + message,
            properties: setParams(optionalParams),
            severityLevel: SeverityLevel.Warning
        };

        if ((process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "development") || (process.env.NODE_ENV?.toLowerCase() === "development")) {
            console.warn(obj.message + ": " + JSON.stringify(obj.properties));
            return;
        }

        let appInsights = appInsightService;
        if (appInsights) {
            appInsights.trackTrace(obj);
        } else {
            console.error("AppInsights is not available...");
        }
    };
};

export const logger = new AppInsightLogger();
